import { formatDate } from '@angular/common';
import { Component, DestroyRef, inject, Inject, OnInit } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';

import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { filter, map } from 'rxjs';
import { SpecialCharacters } from 'src/app/core/enums';
import { OperationTypes } from 'src/app/core/enums/operation-types.enum';
import { LocationProfile } from 'src/app/core/models/admin/location-admin.model';
import { LocationService } from 'src/app/core/services/admin/location/location.service';
import { ArriveService } from 'src/app/core/services/operations/arrive/arrive.service';
import { CommonUtils } from 'src/app/core/utils/common/common.utils';
import { IOption } from '../../form/components/form-select/form-select.model';
import { IMAGE_SRC } from '../../shared-constants';
import { BUTTON_CLICK } from '../warning-modal/warning-modal.constants';
import {
  KEY_ENTRY_FORM_CONTROLS,
  KEY_ENTRY_MODAL_CONSTANTS,
  LOAD_SERVICE_LEVEL_OPTIONS,
  SORT_OPTIONS,
} from './key-entry-modal.constants';

@Component({
  selector: 'app-key-entry-modal',
  templateUrl: './key-entry-modal.component.html',
  styleUrls: ['./key-entry-modal.component.scss'],
  standalone: false,
})
export class KeyEntryModalComponent implements OnInit {
  private readonly _arriveService = inject(ArriveService);
  private readonly destroy = inject(DestroyRef);
  private locationService = inject(LocationService);

  readonly OPERATION_TYPES = OperationTypes;
  readonly CONSTANTS = KEY_ENTRY_MODAL_CONSTANTS;
  readonly FORM_CONTROLS = KEY_ENTRY_FORM_CONTROLS;
  readonly SpecialCharacters = SpecialCharacters;

  constructor(
    @Inject(MAT_DIALOG_DATA) public data: { operationType: OperationTypes },
    private fb: FormBuilder,
    private commonUtils: CommonUtils,
    public readonly dialogRef: MatDialogRef<KeyEntryModalComponent>,
  ) {}

  keyEntryForm!: FormGroup;
  calendarIcon = IMAGE_SRC.CALENDAR_GREY;
  sortOptions = SORT_OPTIONS.sort((a, b) =>
    String(a.value).localeCompare(String(b.value)),
  );
  loadServiceLevelOptions = LOAD_SERVICE_LEVEL_OPTIONS;
  equipmentTypeOptions: IOption[] = [];

  maxDate: Date = new Date();
  minDate: Date = new Date(
    this.maxDate.getFullYear(),
    this.maxDate.getMonth(),
    this.maxDate.getDate() - 5,
  );
  defaultStartTime = this.getFormTime();
  defaultEndTime = '';
  location?: LocationProfile;

  ngOnInit(): void {
    this.initializeForm();
    this.getArriveDepartUldType();
    this.getLocationDetails();
  }

  /**
   * get form time on current date
   *
   * @memberof InterceptSummaryComponent
   */
  getFormTime() {
    const currentDate = new Date();
    const hours = currentDate.getHours().toString();
    const minutes = currentDate.getMinutes().toString();
    const tranformHours = hours.length > 1 ? hours : '0' + hours;
    const tranformMinutes = minutes.length > 1 ? minutes : '0' + minutes;

    return `${tranformHours}:${tranformMinutes}`;
  }

  getLocationDetails() {
    this.locationService
      .getCurrentLocation$()
      .pipe(
        takeUntilDestroyed(this.destroy),
        filter((value) => !!value),
        map((locationConfig) => {
          this.location = locationConfig?.location;
          if (this.data.operationType === OperationTypes.ARRIVE) {
            this.keyEntryForm
              .get(KEY_ENTRY_FORM_CONTROLS.DESTINATION_CC)
              ?.setValue(
                locationConfig.location?.locationDetails.country.isoCode,
              );
            this.keyEntryForm
              .get(KEY_ENTRY_FORM_CONTROLS.DESTINATION_SLIC)
              ?.setValue(locationConfig.location?.locationDetails.pkgSlic);
          } else {
            this.keyEntryForm.patchValue({
              [KEY_ENTRY_FORM_CONTROLS.ORIGIN_CC]:
                locationConfig.location?.locationDetails.country.isoCode,
              [KEY_ENTRY_FORM_CONTROLS.ORIGIN_SLIC]:
                locationConfig.location?.locationDetails.pkgSlic,
            });
          }
        }),
      )
      .subscribe();
  }

  getArriveDepartUldType() {
    this._arriveService
      .getArriveDepartUldType()
      .pipe(
        takeUntilDestroyed(this.destroy),
        map((response) => {
          const uldTypeOptions = response.response || [];
          uldTypeOptions
            .filter((option) => option.arriveable || option.departable)
            .forEach((o) => {
              const option = { ...o, label: o.fulltext, value: o.code };
              this.equipmentTypeOptions.push(option);
            });
          this.keyEntryForm
            .get(this.FORM_CONTROLS.EQUIPMENT_TYPE)
            ?.setValue(this.equipmentTypeOptions[0].value);
        }),
      )
      .subscribe();
  }

  initializeForm() {
    this.keyEntryForm = this.fb.group({
      [KEY_ENTRY_FORM_CONTROLS.ORIGIN_CC]: [
        {
          // For mocking purpose
          value: this.data.operationType === OperationTypes.DEPART ? 'GB' : '',
          disabled: this.data.operationType === OperationTypes.DEPART,
        },
        Validators.compose([Validators.required]),
      ],
      [KEY_ENTRY_FORM_CONTROLS.ORIGIN_SLIC]: [
        {
          // For mocking purpose
          value: this.data.operationType === OperationTypes.DEPART ? '123' : '',
          disabled: this.data.operationType === OperationTypes.DEPART,
        },
        Validators.compose([Validators.required]),
      ],
      [KEY_ENTRY_FORM_CONTROLS.ORIGIN_SORT]: [
        this.sortOptions[0].value,
        Validators.compose([Validators.required]),
      ],
      [KEY_ENTRY_FORM_CONTROLS.DESTINATION_CC]: [
        {
          // For mocking purpose
          value: this.data.operationType === OperationTypes.ARRIVE ? 'GB' : '',
          disabled: this.data.operationType === OperationTypes.ARRIVE,
        },
        Validators.compose([Validators.required]),
      ],
      [KEY_ENTRY_FORM_CONTROLS.DESTINATION_SLIC]: [
        {
          // For mocking purpose
          value: this.data.operationType === OperationTypes.ARRIVE ? '123' : '',
          disabled: this.data.operationType === OperationTypes.ARRIVE,
        },
        Validators.compose([Validators.required]),
      ],
      [KEY_ENTRY_FORM_CONTROLS.DESTINATION_SORT]: [
        this.sortOptions[0].value,
        Validators.compose([Validators.required]),
      ],
      [KEY_ENTRY_FORM_CONTROLS.EQUIPMENT_NUMBER]: [
        '',
        Validators.compose([Validators.required]),
      ],
      [KEY_ENTRY_FORM_CONTROLS.EQUIPMENT_TYPE]: [
        '',
        Validators.compose([Validators.required]),
      ],
      [KEY_ENTRY_FORM_CONTROLS.SORT_DATE]: [
        new Date(),
        Validators.compose([Validators.required]),
      ],
      [KEY_ENTRY_FORM_CONTROLS.LOAD_SERVICE_LEVEL]: [
        this.loadServiceLevelOptions[0].value,
        Validators.compose([Validators.required]),
      ],
      [KEY_ENTRY_FORM_CONTROLS.TIME]: [
        this.getFormTime(),
        Validators.compose([Validators.required]),
      ],
    });
  }

  onExit(): void {
    this.commonUtils
      .openWarningModal({
        header: KEY_ENTRY_MODAL_CONSTANTS.CANCEL_MODAL,
        body: KEY_ENTRY_MODAL_CONSTANTS.BODY,
        confirmButtonText: KEY_ENTRY_MODAL_CONSTANTS.CONFIRM_BUTTON,
        rejectButtonText: KEY_ENTRY_MODAL_CONSTANTS.REJECT_BUTTON,
      })
      .afterClosed()
      .subscribe((resp) => {
        if (resp.button === BUTTON_CLICK.ACCEPT) {
          this.dialogRef.close();
        }
      });
  }

  resetForm(): void {
    this.keyEntryForm.reset();
    if (this.data.operationType === OperationTypes.DEPART) {
      this.keyEntryForm.get(KEY_ENTRY_FORM_CONTROLS.ORIGIN_CC)?.setValue('GB');
      this.keyEntryForm
        .get(KEY_ENTRY_FORM_CONTROLS.ORIGIN_SLIC)
        ?.setValue('123');
    } else {
      this.keyEntryForm
        .get(KEY_ENTRY_FORM_CONTROLS.DESTINATION_CC)
        ?.setValue(this.location?.locationDetails.country);
      this.keyEntryForm
        .get(KEY_ENTRY_FORM_CONTROLS.DESTINATION_SLIC)
        ?.setValue(this.location?.locationDetails.pkgSlic);
    }
  }

  submit() {
    const formData = this.keyEntryForm.getRawValue();
    const time = formData[KEY_ENTRY_FORM_CONTROLS.TIME].split(':').join('');
    const sortDate = formatDate(
      formData[KEY_ENTRY_FORM_CONTROLS.SORT_DATE],
      'yyyy-MM-dd',
      'en-US',
    );
    this.dialogRef.close({
      ...formData,
      [KEY_ENTRY_FORM_CONTROLS.TIME]: time,
      sortDate: sortDate,
      localTime: time,
    });
  }
}
